import { Reveal, SectionLabel, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Activity, ShieldAlert, Network, Cloud, Siren, ClipboardCheck } from "lucide-react";

const skills = [
  {
    id: "SYS_01",
    title: "Security Operations & Monitoring",
    icon: Activity,
    span: "md:col-span-6 lg:col-span-7",
    tags: ["SIEM", "log analysis", "Azure Sentinel", "Splunk", "threat detection"],
    body: "Live monitoring of enterprise telemetry. Tune signal-to-noise, triage alerts, and escalate confirmed threats with a full evidence trail.",
    certs: ["CySA+", "GCIH*", "SEC+"],
  },
  {
    id: "SYS_02",
    title: "Vulnerability Assessment & Pentesting",
    icon: ShieldAlert,
    span: "md:col-span-6 lg:col-span-5",
    tags: ["Nmap", "Metasploit", "Burp Suite", "OWASP", "OSINT"],
    body: "Authorized testing across networks, endpoints, and web apps. Findings written for engineers — actionable, prioritized, with remediation paths.",
    certs: ["PENTEST+", "CNVP", "CSAP"],
  },
  {
    id: "SYS_03",
    title: "Network Security",
    icon: Network,
    span: "md:col-span-4 lg:col-span-4",
    tags: ["TCP/IP", "firewalls", "segmentation", "Wireshark"],
    body: "Design, harden, and monitor network boundaries and internal segmentation.",
    certs: ["NET+", "CNSP"],
  },
  {
    id: "SYS_04",
    title: "Azure Cloud Security",
    icon: Cloud,
    span: "md:col-span-4 lg:col-span-4",
    tags: ["Entra ID", "Defender", "Key Vault", "Azure Policy"],
    body: "Identity, workload, and data protection across Azure. Policy-as-code and least-privilege by default.",
    certs: ["AZ-500", "AZ-900"],
  },
  {
    id: "SYS_05",
    title: "Incident Response",
    icon: Siren,
    span: "md:col-span-4 lg:col-span-4",
    tags: ["triage", "containment", "forensics", "chain-of-custody"],
    body: "First-response playbooks, rapid containment, and clean handoff to remediation and legal.",
    certs: ["GCIH*", "CySA+"],
  },
  {
    id: "SYS_06",
    title: "Governance, Risk & Compliance",
    icon: ClipboardCheck,
    span: "md:col-span-12 lg:col-span-12",
    tags: ["NIST CSF", "ISO 27001", "risk register", "policy", "audit readiness"],
    body: "Translate control frameworks into practical, testable safeguards. Draft policy that engineers will actually read, and evidence that will pass an audit.",
    certs: ["SEC+", "GRC_MC", "FCTP_GRC_AI"],
  },
];

export default function Skills() {
  return (
    <section
      id="skills"
      data-testid="skills-section"
      className="relative py-24 lg:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel id="SYS_02">CAPABILITIES // SPECS</SectionLabel>

        <Reveal>
          <h2 className="font-['Unbounded'] font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F5F5F5] leading-[1.05] max-w-3xl">
            Six disciplines.
            <br />
            <span className="text-[#00FF66]">One analyst.</span>
          </h2>
          <p className="mt-5 font-['JetBrains_Mono'] text-[11px] tracking-[0.25em] text-[#A1A1AA]">
            * = certification in progress
          </p>
        </Reveal>

        <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          {skills.map((s) => {
            const Icon = s.icon;
            return (
              <StaggerItem key={s.id} className={s.span}>
                <div
                  data-testid={`skill-card-${s.id.toLowerCase()}`}
                  className="group relative h-full border border-white/10 bg-[#0A0A0A] p-6 lg:p-8 hover:border-[#00FF66] transition-colors duration-200 corner-bracket overflow-hidden"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.3em] text-[#A1A1AA]">
                      [{s.id}]
                    </span>
                    <Icon
                      size={22}
                      strokeWidth={1.5}
                      className="text-[#A1A1AA] group-hover:text-[#00FF66] transition-colors"
                    />
                  </div>
                  <h3 className="font-['Unbounded'] font-bold text-xl lg:text-2xl text-[#F5F5F5]">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed max-w-lg">
                    {s.body}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className="font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] text-[#00FF66] border border-[#00FF66]/30 px-2 py-1"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-x-3 gap-y-1 font-['JetBrains_Mono'] text-[10px] tracking-[0.25em] text-[#A1A1AA]">
                    <span className="text-[#00FF66]">CERTS //</span>
                    {s.certs.map((c) => (
                      <span key={c}>{c}</span>
                    ))}
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}
